import { useEffect } from "react";
import { useAdminBranding } from "@/hooks/queries";
import { resolveGpsDriveUrl } from "@/lib/api/gpsdrive-client";

// Aplica a identidade visual configurada em Administração > Identidade
// Visual > Tema & Cor / Logotipos no app inteiro, uma vez por sessão.
// Montado uma única vez em __root.tsx (só quando autenticado — reaproveita
// o mesmo useAdminBranding() que a sidebar usa pro logotipo, React Query
// deduplica). `--primary` é uma custom property CSS comum (ver
// styles.css) — sobrescrever com um hex funciona mesmo o valor default
// sendo oklch(), CSS não exige que os dois usem o mesmo espaço de cor.
//
// Sobrescrever só `--primary` não bastava: a sidebar (o elemento mais
// visível da marca, sempre na tela) usa um conjunto de tokens PRÓPRIO
// (--sidebar-primary/--sidebar-ring, ver styles.css) que por padrão tem o
// mesmo valor de --primary mas é uma variável CSS totalmente separada —
// mudar só --primary deixava a sidebar sempre azul, dando a impressão de
// "a cor não muda" mesmo com o valor salvo corretamente no backend. --ring
// (foco de inputs/botões em geral) tinha o mesmo problema.
//
// --accent/--sidebar-accent tinham o MESMO problema, só que mais visível
// ainda: é o token usado pro hover/estado ativo de item de menu, item de
// Select/DropdownMenu/Command e o item ativo da própria sidebar (ver
// data-[active=true]:bg-sidebar-accent em ui/sidebar.tsx) — ou seja, a
// maior parte do que o usuário efetivamente vê pintado ao passar o mouse
// ou navegar continuava azul mesmo com a cor da empresa salva (bug real
// 2026-08-28: "boto es continuam na cor errada" mesmo o Salvar funcionando
// e a cor persistindo certo no banco). color-mix() com "transparent" (em
// vez de um hex sólido calculado) deixa o resultado certo tanto no tema
// claro quanto escuro sem precisar saber qual dos dois está ativo — a
// transparência se mistura com o fundo (claro ou escuro) por baixo.
// Chave lida (antes de qualquer React montar) pelo script inline em
// __root.tsx — mesma cor, aplicada de forma síncrona/bloqueante assim que
// o <head> carrega, pra never mostrar o azul padrão antes da cor real
// (bug relatado 2026-08-28: F5/entrar no sistema mostrava azul por um
// instante e só depois a cor da empresa). Este hook continua sendo a
// fonte de verdade (busca no backend) e apenas atualiza o cache pro
// próximo carregamento; ele não substitui o script inline, os dois juntam
// pra cobrir "primeira visita" (sem cache ainda, inevitável) e "visitas
// seguintes" (cache já aplicado antes do paint).
const BRANDING_COLOR_CACHE_KEY = "gpsdrive:branding-color";

export function useBrandingTheme(): void {
  const { data } = useAdminBranding();

  useEffect(() => {
    if (!data?.primaryColor) return;
    const root = document.documentElement.style;
    const color = data.primaryColor;
    const accent = `color-mix(in srgb, ${color} 15%, transparent)`;
    const sidebarAccent = `color-mix(in srgb, ${color} 18%, transparent)`;
    root.setProperty("--primary", color);
    root.setProperty("--ring", color);
    root.setProperty("--sidebar-primary", color);
    root.setProperty("--sidebar-ring", color);
    root.setProperty("--accent", accent);
    root.setProperty("--accent-foreground", color);
    root.setProperty("--sidebar-accent", sidebarAccent);
    root.setProperty("--sidebar-accent-foreground", color);
    try {
      localStorage.setItem(BRANDING_COLOR_CACHE_KEY, color);
    } catch {
      // localStorage indisponível — sem cache, próximo F5 só pisca uma vez
    }
    return () => {
      root.removeProperty("--primary");
      root.removeProperty("--ring");
      root.removeProperty("--sidebar-primary");
      root.removeProperty("--sidebar-ring");
      root.removeProperty("--accent");
      root.removeProperty("--accent-foreground");
      root.removeProperty("--sidebar-accent");
      root.removeProperty("--sidebar-accent-foreground");
    };
  }, [data?.primaryColor]);

  useEffect(() => {
    if (!data) return;
    // Casa sem favicon → ícone quadrado da GPS Drive, não o logotipo largo.
    applyBrandIcon(data.faviconUrl ?? (data.house ? null : data.logoUrl), data.house, data.serverName ?? data.appName, data.appIconUrl);
    // Logotipo da tela de carregamento do próximo F5 (gps-loader.tsx).
    try {
      if (data.logoUrl) localStorage.setItem(BRAND_LOGO_CACHE_KEY, resolveGpsDriveUrl(data.logoUrl));
      else localStorage.removeItem(BRAND_LOGO_CACHE_KEY);
    } catch {
      // sem cache — o carregamento usa o ícone salvo
    }
  }, [data]);
}

export const BRAND_LOGO_CACHE_KEY = "gpsdrive:brand-logo";

// Logotipo para a tela de carregamento (F5), sem rede: o salvo depois do
// login → o da Tela de Login guardada neste navegador → o ícone do app.
export function readCachedBrandLogo(): string | null {
  try {
    const logo = localStorage.getItem(BRAND_LOGO_CACHE_KEY);
    if (logo) return logo;
    const login = JSON.parse(localStorage.getItem("gpsdrive:login-branding-cache") ?? "null") as
      | { loginLogoUrl?: string | null }
      | null;
    if (login?.loginLogoUrl) return resolveGpsDriveUrl(login.loginLogoUrl);
    return localStorage.getItem(APP_ICON_CACHE_KEY);
  } catch {
    return null;
  }
}

// Ícone padrão quando a identidade não tem favicon nem logo: o da GPS Drive
// só no endereço da casa; empresa nova ganha um ícone neutro (pedido
// 2026-10-29: "toda vez que crio uma empresa o favicon já vem com o da GPS
// Drive"). Também usado pela Tela de Login, com a fatia pública.
const HOUSE_ICON = "/gps-drive-logo.png";

// Títulos das telas são fixos com "GPS Drive" (head de cada rota). Fora do
// endereço da casa troca pelo nome da empresa e continua trocando quando a
// rota muda o <title> (pedido 2026-10-29, Politracker).
let titleObserver: MutationObserver | null = null;
export function applyBrandTitle(name: string | null | undefined, house: boolean | undefined): void {
  if (typeof document === "undefined") return;
  titleObserver?.disconnect();
  titleObserver = null;
  if (house !== false || !name) return;
  const fix = () => {
    if (document.title.includes("GPS Drive")) document.title = document.title.replaceAll("GPS Drive", name);
  };
  fix();
  titleObserver = new MutationObserver(fix);
  titleObserver.observe(document.head, { subtree: true, childList: true, characterData: true });
}
const NEUTRAL_ICON = "/app-icon-neutral.svg";

export function applyBrandIcon(
  raw: string | null | undefined,
  house: boolean | undefined,
  name?: string | null,
  appIcon?: string | null,
): void {
  const href = raw ? resolveGpsDriveUrl(raw) : house ? HOUSE_ICON : NEUTRAL_ICON;
  const homeIcon = appIcon ? resolveGpsDriveUrl(appIcon) : href;
  applyAppIcon(href, name || (house ? "GPS Drive" : undefined), homeIcon);
  try {
    localStorage.setItem(APP_ICON_CACHE_KEY, href);
    localStorage.setItem(HOME_ICON_CACHE_KEY, homeIcon);
  } catch {
    // sem cache, só a primeira tela depois do F5 mostra o ícone antigo
  }
}

const APP_ICON_CACHE_KEY = "gpsdrive:app-icon";
const HOUSE_HOSTS = ["gpsdrive.com.br", "www.gpsdrive.com.br", "admin.gpsdrive.com.br"];
const HOME_ICON_CACHE_KEY = "gpsdrive:home-icon";

// Troca favicon, apple-touch-icon e o ícone do manifest (instalação do PWA)
// pelo logotipo configurado — antes eram fixos no index e só o favicon mudava,
// e só depois do login.
// homeIcon = tela inicial do celular (cai no favicon sem ícone próprio).
// Domínio da empresa serve /api/v1 no mesmo endereço, então usa o
// manifest real do backend; a casa continua no gerado aqui.
function applyAppIcon(href: string, name?: string, homeIcon: string = href): void {
  if (typeof document === "undefined") return;
  const companyHost = !HOUSE_HOSTS.includes(location.hostname);
  const setLink = (rel: string, value: string) => {
    let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement("link");
      link.rel = rel;
      document.head.appendChild(link);
    }
    link.removeAttribute("type");
    link.href = value;
  };
  setLink("icon", href);
  setLink("apple-touch-icon", homeIcon);
  const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (manifestLink && companyHost) {
    manifestLink.href = "/api/v1/public/app-manifest";
  } else if (manifestLink) {
    const appName = name || document.title || "Rastreamento";
    const manifest = {
      name: appName,
      short_name: appName,
      start_url: `${location.origin}/`,
      scope: `${location.origin}/`,
      display: "standalone",
      background_color: "#0b0f14",
      theme_color: "#0b0f14",
      icons: [
        { src: homeIcon, sizes: "192x192", purpose: "any" },
        { src: homeIcon, sizes: "512x512", purpose: "any" },
      ],
    };
    const url = URL.createObjectURL(
      new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" }),
    );
    manifestLink.href = url;
  }
}

// Aplica o último ícone conhecido logo no boot (inclusive na tela de login,
// antes de qualquer requisição autenticada).
export function applyCachedAppIcon(): void {
  try {
    const href = localStorage.getItem(APP_ICON_CACHE_KEY);
    if (href) return applyAppIcon(href, undefined, localStorage.getItem(HOME_ICON_CACHE_KEY) ?? href);
  } catch {
    // ignore
  }
  // Sem cache: GPS Drive só nos endereços da casa, ícone neutro nos demais
  // (até a identidade da empresa responder).
  const house = HOUSE_HOSTS.includes(location.hostname);
  applyAppIcon(house ? HOUSE_ICON : NEUTRAL_ICON);
}
